import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import { API_BASE_URL, SKIN_THEMES } from "../layouts/constants";

const ROLE_OPTIONS = ["Admin", "Supervisor", "Guard", "Manager", "Security"];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "disapproved", label: "Disapproved" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "Guard",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
  year_starts: "",
  year_ends: "",
  dept: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function SentinelAddUser() {
  const navigate = useNavigate();
  useSkin();

  const params = new URLSearchParams(window.location.search);
  const isEdit = params.get("mode") === "edit";
  const memberIdFromQuery = params.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);
  const [staffDbConnected, setStaffDbConnected] = useState(true);

  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedSubmodules, setSelectedSubmodules] = useState({});

  const [faceImage, setFaceImage] = useState(null);
  const [facePreview, setFacePreview] = useState("");
  const [existingFacePath, setExistingFacePath] = useState("");
  const [usePayrollPhoto, setUsePayrollPhoto] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const [licensedUsers, setLicensedUsers] = useState(0);
  const [currentUsers, setCurrentUsers] = useState(0);
  const [limitReached, setLimitReached] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const shouldRequireFaceUpload = !isEdit && !faceImage && !usePayrollPhoto;

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    let userData = {};
    try {
      userData = JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      userData = {};
    }

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const remainingUsers = useMemo(() => {
    if (licensedUsers > 0) return Math.max(0, licensedUsers - currentUsers);
    return null;
  }, [licensedUsers, currentUsers]);

  const selectedGuard = useMemo(() => {
    if (!form.guard_seq) return null;
    return guards.find((guard) => String(getGuardId(guard)) === String(form.guard_seq)) || null;
  }, [guards, form.guard_seq]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, formRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(
          `${API_BASE_URL}/sentinel_users/form-init${
            isEdit ? `?mode=edit&member_id=${encodeURIComponent(memberIdFromQuery)}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!formRes.ok) throw new Error("Failed to fetch sentinel form data");

      const dashboardResult = await dashboardRes.json();
      const formResult = await formRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};
      const payload = formResult?.data || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      setLicensedUsers(Number(payload.licensedUsers || 0));
      setCurrentUsers(Number(payload.currentUsers || 0));
      setLimitReached(!!payload.limitReached);

      const guardList = payload.guards || [];
      setGuards(guardList);
      setStaffDbConnected(payload.staffDbConnected !== false);

      const modulesWithSubs = (payload.modules || []).map((m) => ({
        ...m,
        page_id: String(m.page_id),
        modules: m.modules || m.module_name || "",
        links: m.links || m.module_link || "",
        submodules: payload.subpagesByPage?.[String(m.page_id)] || m.submodules || [],
      }));

      setModules(modulesWithSubs);

      const vm = payload.vm || {};

      setForm({
        ...initialForm,
        fullName: vm.fullName || "",
        email: vm.email || "",
        phone: vm.phone || "",
        designation: vm.designation || "",
        role: vm.role || "Guard",
        status: normalizeStatusValue(vm.status || "pending"),
        username: vm.username || "",
        password: "",
        guard_seq:
          vm.guard_seq === null || vm.guard_seq === undefined ? "" : String(vm.guard_seq),
        memberId: vm.memberId || "",
        year_starts:
          vm.year_starts === null || vm.year_starts === undefined ? "" : String(vm.year_starts),
        year_ends:
          vm.year_ends === null || vm.year_ends === undefined ? "" : String(vm.year_ends),
        dept: vm.dept === null || vm.dept === undefined ? "" : String(vm.dept),
      });

      const savedFacePath = vm.face_image_path || "";
      setExistingFacePath(savedFacePath);

      if (savedFacePath) {
        setFacePreview(savedFacePath);
        setUsePayrollPhoto(false);
      } else if (vm.guard_seq) {
        const editGuard = guardList.find(
          (guard) => String(getGuardId(guard)) === String(vm.guard_seq)
        );

        if (editGuard?.profile_photo_path) {
          setFacePreview(editGuard.profile_photo_path);
          setUsePayrollPhoto(true);
        } else {
          setFacePreview("");
          setUsePayrollPhoto(false);
        }
      } else {
        setFacePreview("");
        setUsePayrollPhoto(false);
      }

      const existingPermissions = payload.existingPermissions || {};
      const modulesState = {};
      const submodulesState = {};

      modulesWithSubs.forEach((module) => {
        const pageId = String(module.page_id);
        const permission = existingPermissions?.[pageId];
        const enabled = !!permission?.main_access;

        modulesState[pageId] = enabled;
        submodulesState[pageId] = enabled ? Object.keys(permission?.submodules || {}) : [];
      });

      setSelectedModules(modulesState);
      setSelectedSubmodules(submodulesState);
    } catch (error) {
      console.error("Sentinel add user fetch error:", error);
      setErrorMessage(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "guard_seq") {
      const guard = guards.find((item) => String(getGuardId(item)) === String(value));
      const payrollPhoto = guard?.profile_photo_path || "";

      if (!faceImage && !existingFacePath) {
        if (payrollPhoto) {
          setFacePreview(payrollPhoto);
          setUsePayrollPhoto(true);
        } else {
          setFacePreview("");
          setUsePayrollPhoto(false);
        }
      }

      setForm((prev) => ({
        ...prev,
        guard_seq: value,
        fullName: guard ? getGuardName(guard) || prev.fullName : prev.fullName,
        designation: guard
          ? guard.designation || guard.fld_designation || prev.designation
          : prev.designation,
      }));

      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFaceImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please upload a valid image file.");
      return;
    }

    setFaceImage(file);
    setFacePreview(URL.createObjectURL(file));
    setUsePayrollPhoto(false);
  };

  const clearFaceImage = () => {
    setFaceImage(null);
    setUsePayrollPhoto(false);
    setFileInputKey((k) => k + 1);

    if (isEdit && existingFacePath) {
      setFacePreview(existingFacePath);
    } else {
      setFacePreview("");
    }
  };

  const toggleModule = (pageId) => {
    const key = String(pageId);

    setSelectedModules((prev) => {
      const next = !prev[key];

      if (!next) {
        setSelectedSubmodules((old) => ({ ...old, [key]: [] }));
      }

      return { ...prev, [key]: next };
    });
  };

  const toggleSubmodule = (pageId, submoduleName) => {
    const key = String(pageId);
    if (!selectedModules[key]) return;

    setSelectedSubmodules((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(submoduleName);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== submoduleName)
          : [...current, submoduleName],
      };
    });
  };

  const buildPayload = () => {
    const perm_module = {};
    const perm_sub = {};

    Object.entries(selectedModules).forEach(([pageId, enabled]) => {
      if (enabled) {
        perm_module[pageId] = true;
        perm_sub[pageId] = selectedSubmodules[pageId] || [];
      }
    });

    return {
      form_mode: isEdit ? "edit" : "create",
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      designation: form.designation,
      role: form.role,
      status: form.status,
      username: form.username,
      password: form.password,
      guard_seq: form.guard_seq === "" ? null : Number(form.guard_seq),
      memberId: form.memberId,
      year_starts: form.year_starts === "" ? null : Number(form.year_starts),
      year_ends: form.year_ends === "" ? null : Number(form.year_ends),
      dept: form.dept === "" ? null : Number(form.dept),
      created_by_admin_id: Number(localStorage.getItem("admin_id") || 1),
      perm_module,
      perm_sub,
    };
  };

  const buildFormData = async () => {
    const payload = buildPayload();

    if (isEdit && !String(form.password || "").trim()) {
      delete payload.password;
    }

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "perm_module" || key === "perm_sub") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    if (faceImage) {
      formData.append("face_image", faceImage);
    } else if (usePayrollPhoto && facePreview) {
      formData.append("use_payroll_photo", "1");
      formData.append("payroll_photo_path", facePreview);
    }

    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName.trim() || !form.username.trim() || !form.role.trim()) {
      setErrorMessage("Please fill all required fields.");
      return;
    }

    if (!form.memberId.trim()) {
      setErrorMessage("Member ID is required.");
      return;
    }

    if (!isEdit && !form.password.trim()) {
      setErrorMessage("Password is required.");
      return;
    }

    if (!isEdit && !faceImage && !usePayrollPhoto) {
      setErrorMessage(
        "Guard face image is required for face verification. Upload an image or select a payroll guard with a photo."
      );
      return;
    }

    if (!isEdit && limitReached) {
      setErrorMessage(
        `User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`
      );
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/sentinel_users/${isEdit ? "update" : "create"}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: await buildFormData(),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel user");
      }

      if (isEdit) {
        setSuccessMessage("Sentinel user updated successfully!");
      } else {
        const createdMemberId =
          data?.data?.memberId || data?.data?.user?.fld_memberId || form.memberId;

        setSuccessMessage(`Sentinel user created successfully! Member ID: ${createdMemberId}`);

        setForm({
          ...initialForm,
          status: "pending",
          role: "Guard",
          memberId: data?.data?.nextMemberId || "",
        });

        setSelectedModules({});
        setSelectedSubmodules({});
        setFaceImage(null);
        setFacePreview("");
        setExistingFacePath("");
        setUsePayrollPhoto(false);
        setFileInputKey((k) => k + 1);
        setCurrentUsers((prev) => prev + 1);

        navigate("/sentinel_user_list");
      }
    } catch (error) {
      console.error("Save sentinel user error:", error);
      setErrorMessage(error.message || "Failed to save sentinel user.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel add user page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_user_list"
      activeMenu="users"
    >
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel User Management
        </h1>

        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit
            ? "Update existing sentinel user details and permissions"
            : "Create a new sentinel user and assign permissions"}
        </p>

        {successMessage && (
          <div className="flex items-center gap-2.5 mb-3.5 px-4 py-3 rounded-xl text-[13px] font-bold bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="flex items-center gap-2.5 mb-3.5 px-4 py-3 rounded-xl text-[13px] font-bold bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Field label="Full Name" required>
                <Input name="fullName" value={form.fullName} onChange={handleChange} required />
              </Field>

              <Field label="Email">
                <Input type="email" name="email" value={form.email} onChange={handleChange} />
              </Field>

              <Field label="Phone">
                <Input name="phone" value={form.phone} onChange={handleChange} />
              </Field>

              <Field label="Designation">
                <Input name="designation" value={form.designation} onChange={handleChange} />
              </Field>

              <Field label="Role" required>
                <Select name="role" value={form.role} onChange={handleChange} required>
                  <option value="">Select role</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Status">
                <Select name="status" value={form.status} onChange={handleChange}>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Year Starts">
                <Input
                  type="number"
                  name="year_starts"
                  value={form.year_starts}
                  onChange={handleChange}
                />
              </Field>

              <Field label="Year Ends">
                <Input
                  type="number"
                  name="year_ends"
                  value={form.year_ends}
                  onChange={handleChange}
                />
              </Field>

              <Field label="Department">
                <Input type="number" name="dept" value={form.dept} onChange={handleChange} />
              </Field>
            </div>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Face Verification
            </h3>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1 mb-5">
              Upload a clear front-face photo for sentinel face verification
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  {isEdit ? "Guard Face Image" : "Guard Face Image *"}
                </label>

                <input
                  key={fileInputKey}
                  type="file"
                  accept="image/*"
                  onChange={handleFaceImageChange}
                  required={shouldRequireFaceUpload}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--accent-faint)] file:px-3 file:py-1.5 file:text-[12px] file:font-bold file:text-[var(--accent)]"
                />

                <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                  Use a clear image with only one face. Avoid mask, blur, side face, or dark lighting.
                </p>
              </div>

              <div>
                <p className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Face Preview
                </p>

                {facePreview ? (
                  <>
                    <img
                      src={getImageUrl(facePreview)}
                      alt="Guard face preview"
                      className="h-36 w-36 rounded-xl border border-[var(--surface-border)] object-cover shadow-sm bg-[var(--surface)]"
                      onError={() => {
                        console.log("Face image failed:", getImageUrl(facePreview));
                        setErrorMessage(
                          `Face image path exists, but image is not loading: ${getImageUrl(facePreview)}`
                        );
                      }}
                    />

                    <p className="mt-1 text-[10px] text-[var(--text-muted)] break-all">
                      {getImageUrl(facePreview)}
                    </p>

                    <button
                      type="button"
                      onClick={clearFaceImage}
                      className="mt-3 rounded-xl border border-[var(--red-border)] bg-[var(--red-faint)] px-3 py-2 text-[11px] font-extrabold text-[var(--red)] hover:bg-[var(--red-border)] transition-colors"
                    >
                      {isEdit && existingFacePath ? "Reset to Existing Image" : "Remove Image"}
                    </button>
                  </>
                ) : (
                  <div className="h-36 w-36 rounded-xl border border-dashed border-[var(--surface-border)] bg-[var(--surface)] flex items-center justify-center text-[12px] font-bold text-[var(--text-muted)]">
                    No Image
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
              Login Credentials
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Field label="Username" required>
                <Input name="username" value={form.username} onChange={handleChange} required />
              </Field>

              <Field label={isEdit ? "Password (leave blank to keep current)" : "Password"} required={!isEdit}>
                <Input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required={!isEdit}
                  placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                />
              </Field>

              <Field label="Guard (link to staff)">
                <Select name="guard_seq" value={form.guard_seq} onChange={handleChange}>
                  <option value="">-- Select Guard --</option>
                  {!staffDbConnected ? (
                    <option value="" disabled>
                      Staff DB connection failed
                    </option>
                  ) : (
                    guards.map((guard) => {
                      const id = getGuardId(guard);
                      const appNo = guard.application_number || guard.staff_code || guard.emp_code || "";
                      const name = getGuardName(guard);
                      const desig = guard.designation || guard.fld_designation || "";
                      const hasPhoto = guard.profile_photo_path ? " 📷" : "";
                      const label = `${appNo ? `${appNo} - ` : ""}${name}${
                        desig ? ` (${desig})` : ""
                      }${hasPhoto}`.trim();

                      return (
                        <option key={id} value={String(id)}>
                          {label || id}
                        </option>
                      );
                    })
                  )}
                </Select>
              </Field>

              <Field label="Member ID">
                <Input name="memberId" value={form.memberId} readOnly placeholder="Auto-generated" />
              </Field>
            </div>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
              Module Access & Permissions
            </h3>

            <div className="space-y-3.5">
              {modules.map((module) => {
                const pageId = String(module.page_id);
                const enabled = !!selectedModules[pageId];
                const selectedSubs = selectedSubmodules[pageId] || [];
                const subs = module.submodules || [];

                return (
                  <div
                    key={pageId}
                    className="border border-[var(--surface-border)] rounded-xl overflow-hidden bg-[var(--card-bg)]"
                  >
                    <div
                      className="px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
                      style={{ background: "var(--btn-bg)" }}
                    >
                      <div className="text-[15px] font-extrabold text-[var(--btn-text)]">
                        {module.modules}
                      </div>

                      <label className="flex items-center gap-2.5 text-[12px] font-bold text-[var(--btn-text)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => toggleModule(pageId)}
                          className="w-[18px] h-[18px] accent-[var(--accent)] cursor-pointer"
                        />
                        Enable Module
                      </label>
                    </div>

                    <div className="p-5">
                      {subs.length === 0 ? (
                        <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                          No sub-modules. Enabling grants module access.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                          {subs.map((sub) => {
                            const subName = sub.submodules || sub.submodule_name || "";
                            const checked = selectedSubs.includes(subName);

                            return (
                              <label
                                key={`${pageId}-${sub.subpage_id || subName}`}
                                className={`flex items-start gap-2.5 border border-[var(--surface-border)] rounded-xl p-3 ${
                                  enabled
                                    ? "bg-[var(--surface)] cursor-pointer hover:bg-[var(--accent-faint)]"
                                    : "bg-[var(--surface)] opacity-50 cursor-not-allowed"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  disabled={!enabled}
                                  checked={checked}
                                  onChange={() => toggleSubmodule(pageId, subName)}
                                  className="mt-1 w-4 h-4 accent-[var(--accent)]"
                                />

                                <div className="text-[13px] font-bold text-[var(--text-primary)]">
                                  {subName}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 flex-wrap mt-1.5 mb-3">
            <button
              type="button"
              onClick={() => navigate("/sentinel_user_list")}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold"
            >
              Back to User List
            </button>

            <button
              type="submit"
              disabled={saving || (!isEdit && limitReached)}
              className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold disabled:opacity-60"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving ? "Saving..." : isEdit ? "Update User" : "+ Create User"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, required = false, children }) {
  return (
    <div>
      <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
        {label}
        {required ? <span className="text-[var(--red)] ml-0.5">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)] disabled:opacity-70 read-only:opacity-70 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] ${className}`}
    >
      {children}
    </select>
  );
}

function normalizeStatusValue(value) {
  const status = String(value || "").toLowerCase();
  if (status === "approved") return "approved";
  if (status === "disapproved") return "disapproved";
  return "pending";
}

function getGuardId(guard) {
  return (
    guard.staff_seq ||
    guard.seq ||
    guard.id ||
    guard.staff_id ||
    guard.emp_id ||
    guard.employee_id ||
    guard.fld_staff_id ||
    guard.fld_emp_id ||
    ""
  );
}

function getGuardName(guard) {
  return (
    guard.staff_name ||
    guard.name ||
    guard.employee_name ||
    guard.full_name ||
    guard.fld_name ||
    ""
  );
}

function getImageUrl(path) {
  if (!path) return "";

  const value = String(path).trim();

  if (value.startsWith("blob:")) return value;

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value.replace("https://eblackdog.net", "https://patrol-react.eblackdog.net");
  }

  const cleanPath = value.startsWith("/") ? value : `/${value}`;

  return `https://patrol-react.eblackdog.net${cleanPath}`;
}